/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/jsx-no-target-blank */
import React, { useEffect, useState } from 'react'
import {
    BodyRow,
    ButtonContainer,
    DeleteButton,
    FileInputButton,
    HeadRow,
    NoData,
    RemapButton,
    Table
} from '../styles/styles'
import List from 'live/components/ui/List'
import { canManageDataImport } from '../permission/Permissions'
import globalConfirmation from 'live/services/global-confirmation'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'
import ActionToggle from 'live/components/ui/action-toggle'
import dateService from 'live/services/date'
import InfoTooltip from './components/InfoTooltip'
import UploadModal from './modals/UploadModal'
import RemapModal from './modals/RemapModal'
import { CSVApi, FileMetadata } from '../api'
import { config } from '../app'

interface Props {
    selected: {
        asset: {
            id: string
            name: string
            state: {
                // eslint-disable-next-line camelcase
                normalization_enabled: {
                    source: string
                }
                rig: {
                    data: {
                        name: string | undefined
                    }
                }
            }
        }
        normalizer: {
            // eslint-disable-next-line camelcase
            event_type: string
        }
    }
}

const formatDuration = (milliseconds: number): string => {
    const totalMinutes = Math.round(milliseconds / 60000)
    const days = Math.floor(totalMinutes / 1440)
    const hours = Math.floor((totalMinutes % 1440) / 60)
    const minutes = totalMinutes % 60

    return `${days}d ${hours}h ${minutes}m`
}

export const DataImportForm: React.FC<Props> = ({ selected }: Props) => {
    const [uploadedFiles, setUploadedFiles] = useState<FileMetadata[]>([])
    const [uploadTrigger, setUploadTrigger] = useState(false)
    const [wellName, setWellName] = useState<string>(selected.asset.name)
    const [rigName, setRigName] = useState<string>(selected.asset.state.rig?.data?.name)
    const [logName, setLogName] = useState<string>('')
    const [logType, setLogType] = useState<string>('date time')
    const [indexColumn, setIndexColumn] = useState<string>('')
    const [indexColumnNumber, setIndexColumnNumber] = useState<number>()
    const [indexFormat, setIndexFormat] = useState<string>()
    const [timezone, setTimezone] = useState<string>()
    const [unitsOnHeader, setUnitsOnHeader] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [formData, setFormData] = useState<FormData>()
    const [header, setHeader] = useState<string[]>([])
    const [filePath, setFilePath] = useState<string>('')
    const [dataLine, setDataLine] = useState<string[]>([])
    const [loading, setLoading] = useState(false)
    const [showRemapModal, setShowRemapModal] = useState(false)
    const [standardChannels, setStandardChannels] = useState<string[]>([])
    const [nullValues, setNullValues] = useState<string[]>([])
    const [sourceName, setSourceName] = useState<string>()
    const [sourceKind, setSourceKind] = useState<string>()
    const [protocolName, setProtocolName] = useState<string>()

    const api = new CSVApi(config)

    useEffect(() => {
        const fetchStandardChannels = async (): Promise<void> => {
            try {
                const data = await api.channels()
                setStandardChannels(data)
            } catch (error) {
                console.error('Error fetching standard channels:', error)
            }
        }

        fetchStandardChannels().then()
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [])

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0]
        if (file) {
            setFilePath(file.name)
            const reader = new FileReader()
            reader.onload = e => {
                const content = e.target?.result as string
                const lines = content.split('\n')
                setHeader(lines[0].split(','))
                if (lines.length >= 3) {
                    setDataLine(lines[2].split(','))
                }
                setLogName(file.name.split('.')[0].toUpperCase())
            }
            reader.readAsText(file)

            const newFormData = new FormData()
            newFormData.append('file', file, file.name)
            setFormData(newFormData)
        }
    }

    const uploadFile = async (formData: FormData): Promise<void> => {
        const isCrew = selected.asset.state.normalization_enabled.source.startsWith('crew')
        setLoading(true)
        try {
            const response = await api.uploadLog(
                {
                    eventTypeName: selected.normalizer.event_type,
                    wellName: !isCrew ? wellName : null,
                    rigName: !isCrew ? rigName : null,
                    wellboreName: !isCrew ? selected.asset.name : null,
                    logName: !isCrew ? logName : null,
                    logType: !isCrew ? logType : null,
                    unitsOnHeader: unitsOnHeader,
                    indexColumn: indexColumn,
                    timezone: timezone,
                    nullValues: nullValues.join(','),
                    sourceName: sourceName,
                    sourceKind: sourceKind,
                    protocolName: protocolName
                },
                { body: formData }
            )

            if (response.status === 200) {
                globalNotification.success(response.statusText)
                setUploadedFiles([])
                setUploadTrigger(prev => !prev) // Trigger useEffect to refresh the list
            } else {
                globalNotification.error(response.statusText)
            }
        } catch (error) {
            globalNotification.error(i18n(`An error occurred while uploading data file: ${error.message}`))
        }
        setLoading(false)
    }

    const handleDelete = async (uid: string): Promise<void> => {
        globalConfirmation
            .confirm(
                <span>{i18n('Are you sure you want to delete this file?')}</span>,
                async () => {
                    try {
                        const response = await api._delete({ uid, eventTypeName: selected.normalizer.event_type })
                        if (response.status === 200) {
                            globalNotification.success(response.statusText)
                            setUploadTrigger(prev => !prev) // Trigger useEffect
                        } else {
                            globalNotification.error(response.statusText)
                        }
                    } catch (error) {
                        console.error('Error deleting file:', error)
                    }
                },
                {
                    btnText: i18n('Yes, delete')
                }
            )
            .then()
    }

    const handleDisableChange = async (uid: string, disabled: boolean): Promise<void> => {
        try {
            const response = await api.disable({ uid, eventTypeName: selected.normalizer.event_type, disabled })

            if (response.status === 200) {
                globalNotification.success(response.statusText)
                setUploadTrigger(prev => !prev) // Trigger useEffect to refresh the list
            } else {
                globalNotification.error(response.statusText)
            }
        } catch (error) {
            console.error('Error updating file status:', error)
        }
    }

    const handleAllowReplay = async (uid: string, allowReplay: boolean): Promise<void> => {
        try {
            const response = await api.allowReplay({ uid, eventTypeName: selected.normalizer.event_type, allowReplay })

            if (response.status === 200) {
                globalNotification.success(response.statusText)
                setUploadTrigger(prev => !prev) // Trigger useEffect to refresh the list
            } else {
                globalNotification.error(response.statusText)
            }
        } catch (error) {
            console.error('Error updating file status:', error)
        }
    }

    const handleRemapClick = (): void => {
        setShowRemapModal(true)
    }

    useEffect(() => {
        // Fetch the list of uploaded files from the service
        const fetchUploadedFiles = async (): Promise<void> => {
            try {
                const data = await api.details({ eventTypeName: selected.normalizer.event_type })

                setUploadedFiles(data)
            } catch (error) {
                console.error('Error fetching uploaded files:', error)
            }
        }

        fetchUploadedFiles().then(r => r)
    }, [selected.normalizer.event_type, uploadTrigger])

    return (
        <>
            {uploadedFiles.length > 0 ? (
                <List
                    sort={false}
                    search={false}
                    filter={false}
                    // @ts-ignore
                    items={uploadedFiles}
                    // @ts-ignore
                    render={(items: FileMetadata[]) => {
                        return (
                            <Table>
                                <HeadRow>
                                    <th></th>
                                    <th>File Name</th>
                                    <th>Date</th>
                                    <th>Uploaded By</th>
                                    <th>Size/Count</th>
                                    <th>Range</th>
                                    <th>Duration</th>
                                    <th>Enabled?</th>
                                    <th>Replay?</th>
                                    <th>Console</th>
                                    <th>Remap</th>
                                    <th>Remove</th>
                                </HeadRow>
                                {items.map(item => (
                                    <BodyRow key={item.uid}>
                                        <InfoTooltip {...item}></InfoTooltip>
                                        <div className={'file-name'}>{item.fileName}</div>
                                        <div>{dateService.formatDate(item.lastModified)}</div>
                                        <div>{item.uploadedBy ?? 'Unknown'}</div>
                                        <div>
                                            {item.fileSize} - {item.lineCount.toLocaleString()} lines
                                        </div>
                                        <div>
                                            {dateService.formatDateTime(item.start)} to{' '}
                                            {dateService.formatDateTime(item.end)}
                                        </div>
                                        <div>{formatDuration(item.end - item.start)}</div>
                                        <div>
                                            <ActionToggle
                                                active={item.disabled === undefined ? false : !item.disabled}
                                                onChange={() => handleDisableChange(item.uid, !item.disabled)}
                                            />
                                        </div>
                                        <div>
                                            <ActionToggle
                                                active={item.allowReplay === undefined ? false : item.allowReplay}
                                                onChange={() => handleAllowReplay(item.uid, !item.allowReplay)}
                                            />
                                        </div>
                                        <div>
                                            <a
                                                href={`/#/console?provider=pipes&preload=false&reducerType=off&expression=raw_${
                                                    selected.normalizer.event_type
                                                } .flags:nocache&span=from%20ts%20${item.start}%20to%20ts%20${
                                                    item.end + 1
                                                }&follow=false&noIndex=true&chart=false&description=Console`}
                                                target="_blank"
                                            >
                                                <i className="fa fa-terminal" aria-hidden="true"></i>
                                            </a>
                                        </div>
                                        <div>
                                            <RemapButton onClick={() => handleRemapClick()}>Remap File</RemapButton>
                                            <RemapModal
                                                showModal={showRemapModal}
                                                setShowModal={setShowRemapModal}
                                                uploadedFile={item}
                                                channels={standardChannels}
                                            />
                                        </div>
                                        <div>
                                            <DeleteButton onClick={() => handleDelete(item.uid)}>
                                                Remove File
                                            </DeleteButton>
                                        </div>
                                    </BodyRow>
                                ))}
                            </Table>
                        )
                    }}
                />
            ) : (
                <NoData>No files uploaded</NoData>
            )}
            <ButtonContainer>
                {canManageDataImport() && (
                    <>
                        <FileInputButton onClick={() => setShowModal(true)}>Upload Data</FileInputButton>
                        <UploadModal
                            source={selected.asset.state.normalization_enabled.source}
                            showModal={showModal}
                            setShowModal={setShowModal}
                            handleFileChange={handleFileChange}
                            filePath={filePath}
                            formData={formData}
                            header={header}
                            wellName={wellName}
                            setWellName={setWellName}
                            rigName={rigName}
                            setRigName={setRigName}
                            logName={logName}
                            setLogName={setLogName}
                            logType={logType}
                            setLogType={setLogType}
                            indexColumn={indexColumn}
                            setIndexColumn={setIndexColumn}
                            setIndexColumnNumber={setIndexColumnNumber}
                            indexColumnNumber={indexColumnNumber}
                            nullValues={nullValues}
                            setNullValues={setNullValues}
                            sourceName={sourceName}
                            setSourceName={setSourceName}
                            sourceKind={sourceKind}
                            setSourceKind={setSourceKind}
                            protocolName={protocolName}
                            setProtocolName={setProtocolName}
                            dataLine={dataLine}
                            indexFormat={indexFormat}
                            setIndexFormat={setIndexFormat}
                            timezone={timezone}
                            setTimezone={setTimezone}
                            unitsOnHeader={unitsOnHeader}
                            setUnitsOnHeader={setUnitsOnHeader}
                            loading={loading}
                            uploadFile={uploadFile}
                        />
                    </>
                )}
            </ButtonContainer>
        </>
    )
}
